import React from 'react';
import { Box, Typography, Button, IconButton, LinearProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { TooltipRenderProps } from 'react-joyride';

/**
 * Brand-themed replacement for react-joyride's default tooltip.
 * Matches the StableTrack brown palette and feels at home in the app chrome.
 */
export const TourStepCard: React.FC<TooltipRenderProps> = ({
  index,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => {
  const progressPercent = size > 0 ? ((index + 1) / size) * 100 : 0;
  const title = (step as any).title as string | undefined;
  const body = step.content as React.ReactNode;

  return (
    <Box
      {...tooltipProps}
      sx={{
        bgcolor: '#FAF7F1',
        color: '#3B2E1A',
        borderRadius: 2.5,
        boxShadow: '0 12px 32px rgba(59,46,26,0.25)',
        border: '1px solid #E8D7BF',
        width: { xs: 'min(92vw, 340px)', sm: 360 },
        overflow: 'hidden',
      }}
      role="tooltip"
    >
      {/* Top header strip with progress */}
      <Box sx={{ bgcolor: '#8B4513', color: '#FFFFFF', px: 2.5, py: 1.25, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', opacity: 0.9 }}
          >
            Step {index + 1} of {size}
          </Typography>
          <IconButton
            aria-label="Close tour"
            {...(closeProps as any)}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.85)', p: 0.25 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 3,
            borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': { bgcolor: '#FFFFFF', borderRadius: 1.5 },
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        {title && (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: '#3B2E1A', lineHeight: 1.3, mb: 0.75 }}
          >
            {title}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ color: '#5A4A3A', lineHeight: 1.55, fontSize: '0.875rem' }}
        >
          {body}
        </Typography>
      </Box>

      {/* Footer actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid #EDE3D3',
          bgcolor: '#F5EDE3',
        }}
      >
        <Button
          {...(skipProps as any)}
          variant="text"
          size="small"
          sx={{
            color: '#8B6B4A',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.75rem',
            minWidth: 0,
            '&:hover': { color: '#6B4F2E', bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Skip tour
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {index > 0 && (
            <Button
              {...(backProps as any)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#C4A478',
                color: '#8B6B4A',
                borderRadius: 16,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                minWidth: 60,
                '&:hover': {
                  borderColor: '#8B6B4A',
                  bgcolor: 'rgba(139,107,74,0.08)',
                },
              }}
            >
              Back
            </Button>
          )}
          <Button
            {...(primaryProps as any)}
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#8B4513',
              color: '#FFFFFF',
              borderRadius: 16,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: 64,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#6B4F2E',
                boxShadow: '0 2px 6px rgba(139,69,19,0.25)',
              },
            }}
          >
            {isLastStep ? 'Done' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
